/**
 * PillarChips — vibe-designer 1.184.0 Phase 7.1.
 *
 * 5 predefined pillar chips (identity / voice / offers / content / qa)
 * matching mind-chat-plugin's pillar enum
 * (apps/mind_chat_plugin/services/database.py:34 — 5 canonical values).
 * Plus "All" reset + free-text escape input.
 *
 * value === null → "All" / no filter
 * value === string → pillar filter active
 *
 * disabled prop renders chips visually but no-ops clicks — used by
 * KnowledgeServiceDetail when service !== 'mind' so the affordance
 * stays discoverable even though the filter is MIND-only enforced.
 *
 * No localStorage — chips are per-query, not per-session.
 */
import { useState, KeyboardEvent } from 'react';

const PILLARS = ['identity', 'voice', 'offers', 'content', 'qa'] as const;
export type Pillar = typeof PILLARS[number];

export interface PillarChipsProps {
  value: string | null;
  onChange: (next: string | null) => void;
  disabled?: boolean;
  className?: string;
}

export function PillarChips({ value, onChange, disabled = false, className = '' }: PillarChipsProps) {
  const [freeText, setFreeText] = useState('');

  const handleClick = (next: string | null) => {
    if (disabled) return;
    onChange(next);
  };

  const handleFreeTextEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    if (disabled) return;
    const trimmed = freeText.trim();
    if (!trimmed) return;
    onChange(trimmed);
  };

  const chipClass = (active: boolean) =>
    `px-3 py-1 text-xs rounded-md border transition-colors ${
      active
        ? 'border-indigo-500 bg-indigo-900/40 text-indigo-100'
        : 'border-slate-700 bg-slate-900/40 text-slate-300 hover:border-slate-500'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`;

  return (
    <div
      className={`flex flex-col gap-2 ${className}`}
      data-testid="pillar-chips"
      data-pillar-disabled={disabled ? 'true' : 'false'}
    >
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => handleClick(null)}
          className={chipClass(value === null)}
          disabled={disabled}
          data-testid="pillar-chip-all"
          title={disabled ? 'Pillar filter is MIND-only (brand-system docs)' : 'Show all (no pillar filter)'}
        >
          All
        </button>
        {PILLARS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => handleClick(p)}
            className={chipClass(value === p)}
            disabled={disabled}
            data-testid={`pillar-chip-${p}`}
            title={disabled ? 'Pillar filter is MIND-only (brand-system docs)' : `Filter to pillar "${p}"`}
          >
            {p}
          </button>
        ))}
        <input
          type="text"
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          onKeyDown={handleFreeTextEnter}
          placeholder="custom pillar (Enter)"
          disabled={disabled}
          data-testid="pillar-chip-freetext"
          className={`px-2 py-1 text-xs rounded-md border bg-slate-900/40 text-slate-200 placeholder:text-slate-500 ${
            disabled ? 'opacity-50 cursor-not-allowed border-slate-700' : 'border-slate-700 focus:border-indigo-500'
          }`}
        />
      </div>
      {value !== null && !disabled && (
        <div
          className="text-xs text-vibe-text-secondary"
          data-testid="pillar-chips-info-banner"
        >
          Filter applies to brand-system docs (identity/voice/offers/content/qa). Older bulk-ingested platform docs may have pillar=null.
        </div>
      )}
    </div>
  );
}
